import { Tabs } from '@clockout-dev/design-elements';
import { LockOutlined } from '@ant-design/icons';
import ConfigurationCard from '../../../common/configurationCard/ConfigurationCard';
import RestrictedPanel from './elements/RestrictedPanel';
import ModeActionPanel from './elements/ModeActionPanel';
import WhitelistEditor from './elements/WhitelistEditor';
import BlockedEditor from './elements/BlockedEditor';
import {
    SEGMENTATION_MODE,
    SEGMENTATION_MODE_TABS,
    MODE_DESCRIPTIONS
} from '../../configuration.constants';
import styles from './styles/userEligibilityForm.module.scss';

// The two tabs backed by client_access_list (ALLOW/DENY member_id
// overrides) — distinct from the other three, which are plain
// segment_policy fields and only need CONFIGURATIONS_MANAGE.
const ACCESS_LIST_TAB_KEYS = [SEGMENTATION_MODE.WHITELIST, SEGMENTATION_MODE.BLOCKED];

/**
 * Service Access Mode card. Lets the operator pick how user eligibility
 * is enforced — by criteria (`RESTRICTED`), by an explicit allow list
 * (`WHITELIST`), block list (`BLOCKED`), wide-open (`OPEN`), or fully
 * turned off (`DISABLED`). Tabs, icons, accent colors and copy all come
 * from `configuration.constants` so this component stays purely compositional.
 */
function ServiceAccessCard({ values = {}, handlers = {}, readOnly = false }) {
    // Session lacks admin:access_list:manage — Segmentation.controller's
    // loadAccessLists() already skipped the fetch for this session (it would
    // 403), so hide the tabs rather than show them empty-but-interactive.
    const canManageAccessList = !!values.canManageAccessList;
    const visibleTabs = canManageAccessList
        ? SEGMENTATION_MODE_TABS
        : SEGMENTATION_MODE_TABS.filter((tab) => !ACCESS_LIST_TAB_KEYS.includes(tab.key));

    const rawSelectedTab = values.selectedTab || SEGMENTATION_MODE.RESTRICTED;
    // The FI's live mode can already BE whitelist/blocked even for a session
    // that can't manage it (segment_policy.mode is FI-wide, the scope is
    // per-session) — never land the panel there without the scope, or it
    // renders as an empty-but-functional editor instead of just a hidden tab.
    const selectedTab = (!canManageAccessList && ACCESS_LIST_TAB_KEYS.includes(rawSelectedTab))
        ? SEGMENTATION_MODE.RESTRICTED
        : rawSelectedTab;
    const mode = values.mode || SEGMENTATION_MODE.RESTRICTED;
    const description = MODE_DESCRIPTIONS[mode];

    // Independent of the general Configurations `readOnly`: an FI can grant
    // admin:access_list:manage without screen:configurations:manage (curate
    // the exception list without touching fee/limits/balance/mode), same as
    // the Team screen's per-action scopes don't depend on screen:team:manage.
    const accessListReadOnly = !canManageAccessList;

    const getPanel = () => {
        const panel = {
            [SEGMENTATION_MODE.WHITELIST]: (
                <WhitelistEditor
                    whitelist={values.whitelist}
                    onAdd={handlers.addToWhitelist}
                    onRemove={handlers.removeFromWhitelist}
                    readOnly={accessListReadOnly}
                    onBulkSuccess={() => {
                        handlers.loadAccessRules?.();
                    }}
                    onBulkUpload={handlers.bulkUploadAccessList}
                    onPollBulkStatus={handlers.getAccessListBulkStatus}
                />
            ),
            [SEGMENTATION_MODE.OPEN]: (
                <ModeActionPanel
                    mode={SEGMENTATION_MODE.OPEN}
                    description={MODE_DESCRIPTIONS[SEGMENTATION_MODE.OPEN]}
                />
            ),
            [SEGMENTATION_MODE.DISABLED]: (
                <ModeActionPanel
                    mode={SEGMENTATION_MODE.DISABLED}
                    description={MODE_DESCRIPTIONS[SEGMENTATION_MODE.DISABLED]}
                />
            ),
            [SEGMENTATION_MODE.RESTRICTED]: (
                <RestrictedPanel values={values} handlers={handlers} savedMode={values.savedMode} readOnly={readOnly} />
            ),
            [SEGMENTATION_MODE.BLOCKED]: (
                <BlockedEditor
                    blockedMembers={values.blockedMembers || []}
                    onAdd={handlers.addBlockedMember}
                    onRemove={handlers.removeBlockedMember}
                    readOnly={accessListReadOnly}
                    onBulkSuccess={() => {
                        handlers.loadAccessRules?.();
                    }}
                    onBulkUpload={handlers.bulkUploadAccessList}
                    onPollBulkStatus={handlers.getAccessListBulkStatus}
                />
            )
        };
        return panel[selectedTab];
    };

    return (
        <ConfigurationCard
            title="Service Access Mode"
            icon={<LockOutlined />}
            description={description}
        >
            <Tabs
                activeKey={selectedTab}
                onChange={handlers.handleServiceAccessTabChange}
                items={visibleTabs}
                classNames={{ strip: styles.modeTabs }}
            />
            <div className={styles.modePanel}>{getPanel()}</div>
        </ConfigurationCard>
    );
}

export default ServiceAccessCard;
